/**
 * A provider read that answered with an error. The pages name the endpoint and the answer instead of
 * rendering an empty list, because "the backend did not answer" and "there is nothing here" are different
 * facts and a claims console must not show the second when it only knows the first (NS-3).
 *
 * No imports on purpose: it runs under the node-only jest config, which cannot load the core barrel.
 */
export class EndpointUnavailableError extends Error {
    constructor(
        /** Method, path and the query parameter NAMES the call relies on — never their values. */
        public readonly endpoint: string,
        public readonly status: number,
        /** The API's error code, e.g. `err.clearinghouse_era_does_not_exist`, when the body has one. */
        public readonly code?: string,
    ) {
        super(`${endpoint} answered ${status}${code ? ` (${code})` : ''}`);
        this.name = 'EndpointUnavailableError';
    }
}

/**
 * The generated `*Raw` methods throw the bare `Response` for any non-2xx. Turns that into an
 * EndpointUnavailableError naming `endpoint`; anything else — an abort, a network failure, a JSON parse
 * error — is returned unchanged, because those are not the backend's answer and must keep their own handling.
 *
 * Reads the body from a clone, so the original stays readable for the app's error middleware.
 */
export async function toEndpointUnavailable(err: unknown, endpoint: string): Promise<unknown> {
    if (!(err instanceof Response)) {
        return err;
    }
    let code: string | undefined;
    try {
        const body = await err.clone().json();
        code = typeof body?.code === 'string' ? body.code : undefined;
    } catch {
        code = undefined;
    }
    return new EndpointUnavailableError(endpoint, err.status, code);
}
